import React, { useEffect, useRef } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import type { WorkspaceMemberRole } from '@mellify/shared';
import { useWorkspaceRole } from '../hooks/useWorkspaceRole';
import { useToast } from '../hooks/useToast';
import { ROUTES } from './routeConfig';

export interface RequireRoleProps {
  /** Array of roles that are allowed to access the wrapped routes */
  allowedRoles: WorkspaceMemberRole[];
  /** Optional custom redirect path (defaults to dashboard) */
  redirectTo?: string;
  /** Optional custom access denied message */
  message?: string;
  /** Optional children to render instead of Outlet */
  children?: React.ReactNode;
}

/**
 * RequireRole
 *
 * Route guard wrapper that enforces role-based access control.
 * Checks the current user's role in the active workspace against
 * the provided allowedRoles array. Redirects unauthorized users
 * to the dashboard with an 'Access Denied' toast notification.
 *
 * @example
 * ```tsx
 * <Route element={<RequireRole allowedRoles={['owner', 'admin']} />}>
 *   <Route path="settings" element={<SettingsPage />} />
 * </Route>
 * ```
 */
export const RequireRole: React.FC<RequireRoleProps> = ({
  allowedRoles,
  redirectTo = ROUTES.DASHBOARD,
  message = 'Access Denied. You do not have permission to view this page.',
  children,
}) => {
  const { role, isLoading, hasRole } = useWorkspaceRole();
  const toast = useToast();
  const location = useLocation();
  const hasShownToast = useRef(false);

  // Ensure allowedRoles is always a valid array to prevent runtime errors
  const safeAllowedRoles = Array.isArray(allowedRoles) ? allowedRoles : [];

  // Determine if user has an allowed role
  const isAuthorized = hasRole && role !== null && safeAllowedRoles.includes(role);

  // Show access denied toast when redirecting (only once per redirect)
  useEffect(() => {
    if (!isLoading && !isAuthorized && hasRole && !hasShownToast.current) {
      hasShownToast.current = true;
      toast.error('Access Denied', message);
    }
  }, [isLoading, isAuthorized, hasRole, toast, message]);

  // Reset toast flag when location changes (user navigated away and back)
  useEffect(() => {
    hasShownToast.current = false;
  }, [location.pathname]);

  // Show loading state while workspace role is being determined
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-dark-900">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-3 border-primary-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-dark-500 dark:text-dark-400">Checking permissions...</p>
        </div>
      </div>
    );
  }

  // If no workspace is selected yet (no role determined), let them through
  // to the dashboard where they can select a workspace
  if (!hasRole) {
    return children ? <>{children}</> : <Outlet />;
  }

  // Redirect unauthorized users
  if (!isAuthorized) {
    return <Navigate to={redirectTo} replace />;
  }

  return children ? <>{children}</> : <Outlet />;
};

RequireRole.displayName = 'RequireRole';